import Phaser from 'phaser';
import type { DeformClipSet } from '@gooddesign/anim-schema';
import type { EngagementLevel } from '@gooddesign/anim-schema';
import { Rig } from './Rig';
import { AttentionChannel } from './attention';
import { actorDepth, reflectionDepth, scaleAt, screenPos } from '../game/ground';

// Npc — a standing character (Order 16.6). NOT an Actor: no belt, no combat
// boxes, no state machine picking locomotion. She holds one ground position and
// performs — the baked idle underneath, the attention channels on top. Her not
// moving IS the acting (arcade_playable_cinema_v0, "the minimal rig"), so this
// class deliberately cannot move her: no GAMEPLAY path writes x or z. The one
// writer is `place()` — the Arcade Authoring shell re-seating her while Matt
// drags her on the stage — which is direction, not locomotion.
//
// She is still a body in the shot's light: the SceneRunner gives her the same
// per-frame facts it gives an Actor — how wet the ground under her is, how deep
// in the plate's shade she stands, how the sun rakes her cast shadow.

export interface NpcConfig {
  scene: Phaser.Scene;
  clips: DeformClipSet;
  /** Where she stands, GROUND (x, z). Fixed for life. */
  x: number;
  z: number;
  /** Rig→world scale at the near kerb (depth scales it from here). */
  rigScale: number;
  facing?: 'left' | 'right';
  /** Attention aim: the player, or a fixed ground point. Absent → she rests. */
  attention?: { target: 'player' | { x: number; z: number }; engagement: EngagementLevel };
}

/** How far, in screen px, a target must be off the figure's axis for the gaze
 *  to be fully lateral (yaw ±1). Wide-ish: attention reads as an inclination,
 *  not a compass needle. */
const YAW_RANGE_PX = 240;
const PITCH_RANGE_PX = 200;

export class Npc {
  readonly id: string;
  readonly rig: Rig;

  /** Where she stands, GROUND (x, z). Written only by the constructor and by
   *  the authoring `place()` — gameplay reads, never writes. */
  private posX: number;
  private posZ: number;

  get x(): number { return this.posX; }
  get z(): number { return this.posZ; }

  private readonly nearScale: number;
  private readonly facing: 1 | -1;
  private readonly attention?: NpcConfig['attention'];
  private readonly channel: AttentionChannel;

  constructor(id: string, cfg: NpcConfig) {
    this.id = id;
    this.posX = cfg.x;
    this.posZ = cfg.z;
    this.nearScale = cfg.rigScale;
    this.facing = cfg.facing === 'left' ? -1 : 1;
    this.attention = cfg.attention;
    this.rig = new Rig(cfg.scene, cfg.clips, {
      scale: cfg.rigScale,
      lit: true,
      reflection: true,
      shadow: true,
    });
    this.channel = new AttentionChannel(this.rig.partKeys(), this.rig.figureRigHeight);
    this.rig.play('idle');
    this.draw();
  }

  /** Play a clip by name ('idle', 'talk'). The beat machinery's handle. */
  play(clip: 'idle' | 'talk'): void {
    this.rig.play(clip);
  }

  /** AUTHORING ONLY: re-seat her on the ground. The Arcade Authoring bridge
   *  calls this while a drag is live so the stage answers the hand in real
   *  time; the scene record is the durable position. Gameplay never calls it. */
  place(x: number, z: number): void {
    this.posX = x;
    this.posZ = z;
    this.draw();
  }

  /** Per-frame light facts, set by the SceneRunner exactly as for an Actor. */
  reflect(strength: number, planeY: number): void {
    this.rig.reflect(strength, planeY);
  }
  castShadow(dx: number, dy: number, strength: number, softness = 0): void {
    this.rig.castShadow(dx, dy, strength, softness);
  }
  setShade(strength: number): void {
    this.rig.shade(strength);
  }

  /** Advance the performance: the attention channels track their target (the
   *  player, usually — she is watching you before you've moved), the baked clip
   *  plays underneath, and the drawing re-projects. `playerX/Z` is the player's
   *  ground position this frame. */
  update(dtSec: number, playerX: number, playerZ: number): void {
    if (this.attention) {
      const t = this.attention.target;
      const target = t === 'player' ? { x: playerX, z: playerZ } : t;
      // Bearings in SCREEN space — a gaze is a picture fact. Yaw is flipped by
      // facing so the channel works in the figure's own left/right.
      const self = screenPos(this.x, this.z, 0);
      const aim = screenPos(target.x, target.z, 0);
      const yaw = ((aim.x - self.x) / YAW_RANGE_PX) * this.facing;
      const pitch = (aim.y - self.y) / PITCH_RANGE_PX;
      this.rig.partOffsets(this.channel.update(dtSec, yaw, pitch, this.attention.engagement));
    }
    this.rig.update(dtSec);
    this.draw();
  }

  private draw(): void {
    const p = screenPos(this.x, this.z, 0);
    const s = scaleAt(this.z) * this.nearScale;
    this.rig.scale(s);
    this.rig.face(this.facing);
    this.rig.place(p.x, p.y);
    this.rig.depth(actorDepth(this.z), reflectionDepth(this.z));
    this.rig.shadeDepth(actorDepth(this.z));
  }

  destroy(): void {
    this.rig.destroy();
  }
}
